/**
 * CSP nonce bootstrap - lets the Content-Security-Policy drop `style-src 'unsafe-inline'`.
 *
 * nginx generates a fresh random nonce per request ($request_id), sends it in the
 * CSP header (`style-src 'self' 'nonce-<value>'`) and writes the same value into
 * <meta name="csp-nonce"> in index.html (see nginx.conf `sub_filter`).
 *
 * The UI libraries in this app (MUI/emotion, antd cssinjs, styled-components,
 * PrimeReact, ApexCharts) inject <style> tags at runtime. This module hands the
 * nonce to each of them so those tags are allowed by the policy.
 *
 * MUST be the first import in src/index.tsx so it runs before any library injects styles.
 * In local development (`npm start`) there is no nginx and no CSP, the placeholder is
 * not replaced, and this module does nothing.
 */
import PrimeReact from "primereact/api";

const NONCE_PLACEHOLDER = "__CSP_NONCE__";

const readNonce = (): string | null => {
	const value = document.querySelector<HTMLMetaElement>('meta[name="csp-nonce"]')?.content;
	if (!value || value === NONCE_PLACEHOLDER) return null;
	return value;
};

export const cspNonce = readNonce();

if (cspNonce) {
	const nonce = cspNonce;

	// webpack: lazy-loaded chunks + styled-components read this to stamp their <style>/<script> tags.
	__webpack_nonce__ = nonce;

	// PrimeReact: dynamic <style> tags (DataTable responsive layout, Dialog/Calendar breakpoints).
	PrimeReact.nonce = nonce;

	// ApexCharts: global default for every chart's injected stylesheet.
	window.Apex = { ...(window.Apex || {}), chart: { ...(window.Apex?.chart || {}), nonce } };

	// Safety net for every other runtime-created <style> (emotion/MUI, antd static
	// message/notification/Modal.confirm that render outside ConfigProvider, rc-* components).
	const originalCreateElement = Document.prototype.createElement;
	Document.prototype.createElement = function (this: Document, tagName: string, options?: ElementCreationOptions) {
		const element = originalCreateElement.call(this, tagName, options);
		if (typeof tagName === "string" && tagName.toLowerCase() === "style") {
			element.setAttribute("nonce", nonce);
		}
		return element;
	} as typeof Document.prototype.createElement;

	// Without 'unsafe-inline' the browser ignores `el.setAttribute("style", ...)` (antd TextArea
	// autoSize measures with a hidden textarea styled this way). Applying the same value through
	// the CSSOM (`el.style.cssText`) is permitted by CSP and renders identically. Markup injected
	// through HTML (innerHTML / dangerouslySetInnerHTML) is still blocked - that is the protection.
	const originalSetAttribute = Element.prototype.setAttribute;
	Element.prototype.setAttribute = function (this: Element, name: string, value: string) {
		if (typeof name === "string" && name.toLowerCase() === "style" && "style" in this) {
			(this as HTMLElement).style.cssText = String(value);
			return;
		}
		originalSetAttribute.call(this, name, value);
	};
}
